import type { ApiGlossaryTerm } from '@/types/api'

/**
 * Checks whether `ch` is a word character (letter, digit, or underscore).
 * Used for whole-word boundary detection without relying on regex \b.
 */
function isWordChar(ch: string): boolean {
  return /\w/.test(ch)
}

/**
 * Returns true if the character at `index` in `str` is inside an HTML tag
 * (i.e. between a `<` and its corresponding `>`).
 *
 * We walk backward from `index` to find the nearest `<` or `>`.
 * If we find `<` first we're inside a tag; if we find `>` (or reach the
 * start of the string) we're outside.
 */
function isInsideHtmlTag(str: string, index: number): boolean {
  for (let i = index - 1; i >= 0; i--) {
    const ch = str[i]
    if (ch === '>') return false
    if (ch === '<') return true
  }
  return false
}

interface TermMatch {
  start: number
  end: number   // exclusive
  termId: number
  matchedText: string
}

/**
 * Performs a single-pass scan over `html` to find the first non-overlapping
 * occurrence of each glossary term that:
 *   - Is not inside an HTML tag attribute (e.g. href="…")
 *   - Is not inside an already-injected <span class="glossary-term"> block
 *   - Has not already been seen on this page (tracked by `seenTermIds`)
 *   - Matches as a whole word (character boundaries on both sides)
 *
 * Terms are pre-sorted longest-first so that when two terms start at the
 * same position the longer one wins.  Once a term's ID is recorded in
 * `seenTermIds` it will never be matched again (even in later RichContent
 * instances on the same page).
 *
 * `seenTermIds` is **mutated in-place** — callers that share a Set across
 * multiple calls achieve the "first instance per page" behaviour.
 */
export function injectGlossarySpans(
  html: string,
  terms: ApiGlossaryTerm[],
  seenTermIds: Set<number>,
): string {
  if (!html || terms.length === 0) return html

  // Sort longest-first so longer terms take precedence over sub-terms.
  // Build lowercase versions for case-insensitive comparison.
  const sorted = [...terms]
    .sort((a, b) => b.term.length - a.term.length)
    .map((t) => ({ ...t, termLower: t.term.toLowerCase() }))

  const htmlLower = html.toLowerCase()
  const len = html.length

  // Collect all valid first-occurrences, one per unseen term.
  // We work through the string position by position only for terms we still
  // need to find; once a term is matched it is added to seenTermIds and
  // removed from the candidate list.
  const matches: TermMatch[] = []

  // Track which character ranges are already "occupied" by a match so we
  // never produce overlapping spans (handles sub-term overlap).
  // We use a simple sorted interval list; since we process left-to-right and
  // terms are sorted longest-first, the first valid match at any position wins.
  const occupied: Array<[number, number]> = []  // [start, end) pairs, sorted by start

  function isOccupied(start: number, end: number): boolean {
    for (const [os, oe] of occupied) {
      if (os >= end) break   // occupied list is sorted; no need to look further
      if (start < oe && end > os) return true
    }
    return false
  }

  // For each unseen term, find its first valid occurrence in the HTML.
  // We iterate terms in longest-first order so that when we later check
  // isOccupied, shorter sub-terms cannot clobber an already-found longer match.
  for (const t of sorted) {
    if (seenTermIds.has(t.id)) continue

    const tLen = t.termLower.length
    let searchFrom = 0

    while (searchFrom <= len - tLen) {
      const idx = htmlLower.indexOf(t.termLower, searchFrom)
      if (idx === -1) break

      const end = idx + tLen

      // Whole-word check
      const charBefore = idx > 0 ? html[idx - 1] : ''
      const charAfter = end < len ? html[end] : ''
      if (isWordChar(charBefore) || isWordChar(charAfter)) {
        searchFrom = idx + 1
        continue
      }

      // Skip if inside an HTML tag (e.g. tag attribute value)
      if (isInsideHtmlTag(html, idx)) {
        searchFrom = idx + 1
        continue
      }

      // Skip if overlapping with an already-matched span
      if (isOccupied(idx, end)) {
        searchFrom = idx + 1
        continue
      }

      // Valid match — record it
      matches.push({ start: idx, end, termId: t.id, matchedText: html.slice(idx, end) })
      // Insert into occupied (keep sorted by start)
      const insertAt = occupied.findIndex(([s]) => s > idx)
      if (insertAt === -1) occupied.push([idx, end])
      else occupied.splice(insertAt, 0, [idx, end])
      seenTermIds.add(t.id)
      break
    }
  }

  if (matches.length === 0) return html

  // Sort matches by start position so we can rebuild the string in one pass.
  matches.sort((a, b) => a.start - b.start)

  // Build the result string by interleaving unchanged text with wrapped spans.
  let result = ''
  let cursor = 0
  for (const m of matches) {
    result += html.slice(cursor, m.start)
    result += `<span class="glossary-term" data-term-id="${m.termId}" tabindex="0">${m.matchedText}</span>`
    cursor = m.end
  }
  result += html.slice(cursor)

  return result
}
